import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Clock, Download, FileX2, Info, Loader2, Mail, Phone, XCircle } from "lucide-react";
import nelgatesLogo from "@/icons/nelgates-logo-horizontal.png";
import { cn } from "@/lib/utils";
import { ProposalDocument } from "@/components/proposals/ProposalDocument";
import { PublicDecisionPanel } from "@/components/proposals/PublicDecisionPanel";
import type { PublicProposal } from "@/types/proposal";
import { dateLocale, withClock } from "@/lib/dateTime";

const fmtDate = (d?: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "long", year: "numeric" }) : "";

const fmtDateTime = (d: string) =>
  new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }));

/** Reading time is reported every this many seconds while the page is visible */
const PING_EVERY_S = 15;

/**
 * The customer's view of a proposal (/p/<secret>). No login: the link is the key. Plain fetch is used
 * (not the CRM api helper) so a signed-out visitor never triggers the CRM's session handling.
 */
export function PublicProposalPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicProposal | null>(null);
  const [problem, setProblem] = useState("");
  const [downloading, setDownloading] = useState(false);
  const visibleSince = useRef<number | null>(null);

  // Never indexed, and the secret link is never sent as a referrer
  useEffect(() => {
    const metas = [
      ["robots", "noindex, nofollow"],
      ["referrer", "no-referrer"],
    ].map(([name, content]) => {
      const m = document.createElement("meta");
      m.name = name!;
      m.content = content!;
      document.head.appendChild(m);
      return m;
    });
    // The customer page is always light, whatever theme a signed-in rep uses in the CRM
    const wasDark = document.documentElement.classList.contains("dark");
    document.documentElement.classList.remove("dark");
    return () => {
      metas.forEach((m) => m.remove());
      if (wasDark) document.documentElement.classList.add("dark");
    };
  }, []);

  const load = () =>
    fetch(`/api/Public/Proposal/${encodeURIComponent(token)}`, { credentials: "include", headers: { Accept: "application/json" } })
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.Message || body?.message || "This link isn't valid.");
        setData(body as PublicProposal);
        document.title = body?.document?.title || body?.title || "Proposal";
      })
      .catch((err) => setProblem(err.message || "This link isn't valid."));

  useEffect(() => {
    load();
  }, [token]);

  // Reading time: count only while the page is visible, and note which section is being read
  useEffect(() => {
    const viewId = data?.view_id;
    if (!viewId) return;
    const bySection: Record<string, number> = {};
    let active: string | null = null;
    // The section crossing a line 40% down the window is the one being read
    const locate = () => {
      const line = window.innerHeight * 0.4;
      const els = Array.from(document.querySelectorAll<HTMLElement>("[data-section-id]"));
      let found: string | null = els[0]?.dataset.sectionId || null;
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.top <= line) found = el.dataset.sectionId || found;
      }
      active = found;
    };
    locate();
    const tick = setInterval(() => {
      if (document.visibilityState === "visible" && active) bySection[active] = (bySection[active] || 0) + 1;
    }, 1000);
    const send = (keepalive = false) => {
      if (visibleSince.current === null) return;
      const seconds = Math.round((Date.now() - visibleSince.current) / 1000);
      visibleSince.current = document.visibilityState === "visible" ? Date.now() : null;
      if (seconds <= 0) return;
      const sections = { ...bySection };
      for (const k of Object.keys(bySection)) delete bySection[k];
      fetch(`/api/Public/Proposal/${encodeURIComponent(token)}/ping`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ view_id: viewId, seconds, sections }),
        keepalive,
      }).catch(() => undefined);
    };
    visibleSince.current = document.visibilityState === "visible" ? Date.now() : null;
    const timer = setInterval(() => send(), PING_EVERY_S * 1000);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") send(true);
      else visibleSince.current = Date.now();
    };
    const onLeave = () => send(true);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onLeave);
    window.addEventListener("scroll", locate, { passive: true });
    window.addEventListener("resize", locate);
    return () => {
      clearInterval(timer);
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onLeave);
      window.removeEventListener("scroll", locate);
      window.removeEventListener("resize", locate);
    };
  }, [data?.view_id, token]);

  // The contents list highlights the section being read
  const [activeSection, setActiveSection] = useState<string | null>(null);
  useEffect(() => {
    if (!data?.document) return;
    const locate = () => {
      const line = window.innerHeight * 0.35;
      let found: string | null = null;
      for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-section-id]"))) {
        if (el.getBoundingClientRect().top <= line) found = el.dataset.sectionId || found;
      }
      setActiveSection(found);
    };
    locate();
    window.addEventListener("scroll", locate, { passive: true });
    return () => window.removeEventListener("scroll", locate);
  }, [data?.document]);

  const download = () => {
    setDownloading(true);
    // A plain navigation lets the browser handle the download (and works on phones)
    window.location.href = `/api/Public/Proposal/${encodeURIComponent(token)}/pdf`;
    setTimeout(() => setDownloading(false), 2500);
  };

  const rep = data?.contact_person;
  const initials = (rep?.name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const waDigits = (rep?.phone || "").replace(/\D/g, "");
  const contactBlock = rep && (
    <section aria-label="Your contact" className="space-y-2 rounded-xl border bg-white p-4 text-sm text-foreground">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Your contact</div>
      <div className="flex items-center gap-2.5">
        <span className="flex size-9 items-center justify-center rounded-full bg-gold text-xs font-bold text-sidebar">{initials || "?"}</span>
        <span className="flex flex-col leading-tight">
          <span className="font-semibold">{rep.name}</span>
          <span className="text-xs text-muted-foreground">{data?.company.name}</span>
        </span>
      </div>
      <div className="flex flex-col gap-1 text-[13px]">
        {rep.phone && (
          <a href={`tel:${rep.phone}`} className="inline-flex items-center gap-1.5 text-primary hover:underline">
            <Phone className="size-3.5" /> {rep.phone}
          </a>
        )}
        {waDigits.length >= 10 && (
          <a href={`https://wa.me/${waDigits.length === 10 ? `91${waDigits}` : waDigits}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
            WhatsApp {rep.name.split(" ")[0]}
          </a>
        )}
        {rep.email && (
          <a href={`mailto:${rep.email}`} className="inline-flex items-center gap-1.5 break-all text-primary hover:underline">
            <Mail className="size-3.5 shrink-0" /> {rep.email}
          </a>
        )}
      </div>
    </section>
  );
  const toc = (data?.document?.sections || []).filter((x) => x.type !== "cover");

  if (problem) {
    return (
      <Shell>
        <Notice icon={FileX2} title="This link can't be opened" body={problem} />
      </Shell>
    );
  }
  if (!data) {
    return (
      <Shell>
        <div className="py-24 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </Shell>
    );
  }

  const open = data.state === "open" && !!data.document;
  return (
    <Shell
      company={data.company.name}
      logoSrc={data.company.has_logo ? `/api/Public/Proposal/${encodeURIComponent(token)}/logo` : undefined}
      title={`Proposal for ${data.document?.customer.company || data.title || "you"}`}
      subtitle={`${data.display_number}${data.valid_until ? ` · valid until ${fmtDate(data.valid_until)}` : ""}`}
      action={
        data.document && (
          <>
            <button
              type="button"
              onClick={download}
              disabled={downloading}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border bg-white px-3.5 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-70"
            >
              {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              {data.state === "accepted" ? "Download signed PDF" : "Download PDF"}
            </button>
            {rep?.email && (
              <a
                href={`mailto:${rep.email}?subject=${encodeURIComponent(`Question about proposal ${data.display_number}`)}`}
                className="hidden h-9 items-center rounded-lg border border-border bg-white px-3.5 text-[13px] font-medium text-foreground hover:bg-muted sm:inline-flex"
              >
                Ask a question
              </a>
            )}
            {open && (
              <a href="#accept" className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-[13px] font-semibold text-white hover:bg-primary/90">
                Review and accept
              </a>
            )}
          </>
        )
      }
    >
      <div className="mx-auto flex w-full max-w-[1180px] flex-wrap items-start justify-center gap-6">
        {toc.length > 1 && (
          <nav aria-label="Contents" className="sticky top-20 hidden w-[200px] shrink-0 flex-col gap-0.5 pt-2 lg:flex">
            <span className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contents</span>
            {toc.map((sec, i) => (
              <a
                key={sec.id}
                href={`#${sec.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector(`[data-section-id="${sec.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={cn("flex gap-2 rounded-md px-2.5 py-1.5 text-[13px]", activeSection === sec.id ? "bg-white font-semibold text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground")}
              >
                <span className="font-mono text-xs text-muted-foreground/70">{i + 1}</span>
                {sec.title}
              </a>
            ))}
          </nav>
        )}

        <div className="min-w-0 max-w-3xl flex-[1_1_560px] space-y-4">
          {data.internal && (
            <div className="flex gap-2 rounded-xl border border-primary/20 bg-primary/10 px-4 py-2.5 text-xs text-primary">
              <Info className="size-4 shrink-0" /> You're signed in to the CRM, so this visit isn't counted as a customer view.
            </div>
          )}
          {data.state === "expired" && (
            <Notice
              icon={Clock}
              title={`${data.title || "This proposal"} has expired`}
              body={`It was valid until ${fmtDate(data.valid_until)}. Please contact ${rep?.name || "us"} for an updated proposal.`}
            />
          )}
          {data.state === "superseded" && (
            <Notice icon={AlertTriangle} title="This proposal was replaced" body={`A newer version of ${data.display_number} is being prepared. ${rep?.name || "We"} will send you the latest one.`} />
          )}
          {data.state === "accepted" && data.decision && (
            <div className="flex gap-3 rounded-xl border border-success/20 bg-success-soft px-5 py-4 text-sm text-success">
              <CheckCircle2 className="size-5 shrink-0 text-success" />
              <div>
                <div className="font-semibold">
                  Accepted by {data.decision.signer_name}
                  {data.decision.signer_title ? `, ${data.decision.signer_title}` : ""} on {fmtDateTime(data.decision.decided_at)}
                </div>
                <div className="mt-0.5 text-success">Thank you. The signed copy is available above, and we'll be in touch about the next steps.</div>
              </div>
            </div>
          )}
          {data.state === "declined" && data.decision && (
            <div className="flex gap-3 rounded-xl border border-border bg-white px-5 py-4 text-sm text-foreground">
              <XCircle className="size-5 shrink-0 text-muted-foreground" />
              <div>
                <div className="font-semibold text-foreground">Declined on {fmtDateTime(data.decision.decided_at)}</div>
                {data.decision.reason && <div className="mt-0.5">"{data.decision.reason}"</div>}
                <div className="mt-0.5">Thank you for letting us know. If anything changes, {rep?.name || "we"} will be glad to help.</div>
              </div>
            </div>
          )}
          {data.document?.source === "UPLOADED" && (
            <div className="overflow-hidden rounded-xl border border-border bg-white shadow-xs">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-foreground">{data.document.title}</div>
                  <div className="text-xs text-muted-foreground">{data.display_number}{data.valid_until ? ` · valid until ${fmtDate(data.valid_until)}` : ""}</div>
                </div>
                <button type="button" onClick={download} disabled={downloading} className="inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold hover:bg-muted">
                  <Download className="size-3.5" /> Download PDF
                </button>
              </div>
              {data.state !== "expired" && data.state !== "superseded" && (
                <iframe title={data.document.title} src={`/api/Public/Proposal/${encodeURIComponent(token)}/pdf?view=1#toolbar=0`} className="block h-[75vh] w-full bg-muted/40" />
              )}
            </div>
          )}
          {data.document && data.document.source !== "UPLOADED" && <ProposalDocument
              doc={data.document}
              logoSrc={data.company.has_logo ? `/api/Public/Proposal/${encodeURIComponent(token)}/logo` : ""}
              letterhead={data.letterhead ? { src: `/api/Public/Proposal/${encodeURIComponent(token)}/letterhead`, top: data.letterhead.top, bottom: data.letterhead.bottom } : null}
            />}
          {open && data.consent_text && (
            <PublicDecisionPanel
              token={token}
              displayNumber={data.display_number}
              customer={data.document!.customer.company}
              recipientName={data.recipient_name}
              consentText={data.consent_text}
              contactName={rep?.name}
              contactEmail={rep?.email}
              disabledReason={data.internal ? "You're signed in to the CRM, so you can't accept for the customer. To record a decision they made elsewhere, use Record decision on the proposal." : undefined}
              onDecided={load}
            />
          )}
          <div className="lg:hidden">{contactBlock}</div>
        </div>

        <aside className="sticky top-20 hidden w-[220px] shrink-0 pt-2 lg:block">{contactBlock}</aside>
      </div>
    </Shell>
  );
}

function Shell({
  company,
  logoSrc,
  title,
  subtitle,
  action,
  children,
}: {
  company?: string;
  logoSrc?: string;
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-muted text-foreground">
      <header className="sticky top-0 z-10 border-b bg-white">
        <div className="mx-auto flex min-h-14 max-w-[1180px] flex-wrap items-center gap-x-3.5 gap-y-2 px-4 py-2">
          <span className="flex min-w-[220px] flex-1 items-center gap-2.5">
            {logoSrc ? <img src={logoSrc} alt={company || "Company logo"} className="h-8 max-w-[150px] object-contain" /> : <img src={nelgatesLogo} alt="Nelgates" className="h-8 object-contain" />}
            {title && (
              <span className="flex flex-col leading-tight">
                <span className="font-semibold">{title}</span>
                {subtitle && <span className="font-mono text-xs text-muted-foreground">{subtitle}</span>}
              </span>
            )}
          </span>
          <span className="flex flex-wrap items-center gap-2">{action}</span>
        </div>
      </header>
      <main className="px-3 py-6 sm:px-4">{children}</main>
    </div>
  );
}

function Notice({ icon: Icon, title, body }: { icon: React.ElementType; title: string; body: string }) {
  return (
    <div className="mx-auto max-w-xl rounded-xl border bg-white p-8 text-center">
      <Icon className="mx-auto size-10 text-muted-foreground" />
      <h1 className="mt-3 text-lg font-bold text-foreground">{title}</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
